import React from "react";
import { TransactionColumn } from "@common/types/views/transactionViews";
import { useActiveWorkspace } from "@hooks/useActiveWorkspace";
import { useEntities } from "@hooks/useEntities";
import { useIsGlFirst } from "@hooks/useIsGlFirst";
import { saveBlobResponse, useFileDownload } from "@lib/fileDownload";
import { Menu } from "@design-system/Menu/Menu.lib";
import { Button } from "@design-system/Button/Button";
import { ExportMenuItem } from "@design-system/ExportMenuItem/ExportMenuItem";
import { getTransactionsCsv, getTransactionsXlsx } from "@lib/transactions";
import { DownloadIcon } from "@icons";
import { convertFiltersToDto } from "./useTransactionsQuery";
import { useFiltersActive } from "./useFiltersActive";
import { TransactionsFilters } from "./filters/lib";
import { getSortExpression, TransactionSortValue } from "./useSort";

enum TransactionsExportFormat {
    CSV = "csv",
    XLSX = "xlsx",
}

/**
 * Default columns for the "All transactions" export. Mirrors the backend
 * fallback so non-GL-first behavior is unchanged.
 */
const DEFAULT_EXPORT_COLUMNS: TransactionColumn[] = [
    TransactionColumn.DATE,
    TransactionColumn.COUNTERPARTY,
    TransactionColumn.BANK_DESCRIPTION,
    TransactionColumn.CATEGORY,
    TransactionColumn.AMOUNT,
];

/**
 * GL-first workspaces classify by general ledger account rather than category,
 * so the export swaps the Category column for the general ledger account.
 */
const GL_FIRST_EXPORT_COLUMNS: TransactionColumn[] = [
    TransactionColumn.DATE,
    TransactionColumn.COUNTERPARTY,
    TransactionColumn.BANK_DESCRIPTION,
    TransactionColumn.LEDGER,
    TransactionColumn.AMOUNT,
];

interface Props {
    currentFilters: TransactionsFilters;
    currentColumns: TransactionColumn[];
    currentSort: TransactionSortValue;
}

interface UseTransactionsExportDownloadParams {
    format: TransactionsExportFormat;
    withFilters: boolean;
    currentFilters: TransactionsFilters;
    currentColumns: TransactionColumn[];
    currentSort: TransactionSortValue;
}

function useTransactionsExportDownload({
    format,
    withFilters,
    currentFilters,
    currentColumns,
    currentSort,
}: UseTransactionsExportDownloadParams) {
    const entities = useEntities();
    const activeWorkspace = useActiveWorkspace();
    const { enabled: isGlFirst } = useIsGlFirst();

    return useFileDownload(async () => {
        if (!activeWorkspace) {
            return;
        }

        const filters = convertFiltersToDto(
            withFilters ? currentFilters : {},
            entities,
        );
        const defaultColumns = isGlFirst
            ? GL_FIRST_EXPORT_COLUMNS
            : DEFAULT_EXPORT_COLUMNS;
        const payload = {
            workspaceId: activeWorkspace.id,
            filters,
            columns: withFilters ? currentColumns : defaultColumns,
            sort: getSortExpression(currentSort),
        };

        if (format === TransactionsExportFormat.CSV) {
            const response = await getTransactionsCsv(payload);
            saveBlobResponse(response, "transactions.csv");
            return;
        }

        const response = await getTransactionsXlsx(payload);
        saveBlobResponse(response, "transactions.xlsx");
    });
}

export const TransactionsExport: React.FC<Props> = ({
    currentFilters,
    currentColumns,
    currentSort,
}) => {
    const filtersActive = useFiltersActive(currentFilters);

    const downloadFilteredCsv = useTransactionsExportDownload({
        format: TransactionsExportFormat.CSV,
        withFilters: true,
        currentFilters,
        currentColumns,
        currentSort,
    });
    const downloadFilteredXlsx = useTransactionsExportDownload({
        format: TransactionsExportFormat.XLSX,
        withFilters: true,
        currentFilters,
        currentColumns,
        currentSort,
    });
    const downloadAllCsv = useTransactionsExportDownload({
        format: TransactionsExportFormat.CSV,
        withFilters: false,
        currentFilters,
        currentColumns,
        currentSort,
    });
    const downloadAllXlsx = useTransactionsExportDownload({
        format: TransactionsExportFormat.XLSX,
        withFilters: false,
        currentFilters,
        currentColumns,
        currentSort,
    });

    return (
        <Menu.Root
            trigger={
                <Button
                    variant="primary"
                    iconOnly
                    size="md"
                    aria-label="Export transactions"
                >
                    <DownloadIcon />
                </Button>
            }
        >
            {filtersActive ? (
                <>
                    <ExportMenuItem
                        isDownloading={downloadFilteredCsv.isDownloading}
                        onClick={() => downloadFilteredCsv.download()}
                    >
                        Filtered transactions (.csv)
                    </ExportMenuItem>
                    <ExportMenuItem
                        isDownloading={downloadFilteredXlsx.isDownloading}
                        onClick={() => downloadFilteredXlsx.download()}
                    >
                        Filtered transactions (.xlsx)
                    </ExportMenuItem>
                </>
            ) : null}

            <ExportMenuItem
                isDownloading={downloadAllCsv.isDownloading}
                onClick={() => downloadAllCsv.download()}
            >
                All transactions (.csv)
            </ExportMenuItem>

            <ExportMenuItem
                isDownloading={downloadAllXlsx.isDownloading}
                onClick={() => downloadAllXlsx.download()}
            >
                All transactions (.xlsx)
            </ExportMenuItem>
        </Menu.Root>
    );
};
